import { QueryClient, useMutation } from "@tanstack/react-query"
import { MainLoggedInLayout } from "../../components/layouts/main.loggedIn.layout"
import { useContext, useEffect, useState } from "react"
import { UserContext } from "../../context/UserContext"
import { 
    Button, 
    Card, 
    CardActions, 
    CardContent, 
    CardHeader, 
    FormControl, 
    FormControlLabel, 
    FormLabel, 
    Grid, 
    TextField,
    Typography,
    Box,
    Divider,
    Paper,
    Chip,
    Stack,
    Alert,
    IconButton,
    Radio,
    RadioGroup,
    Container
} from "@mui/material"
import { 
    Event as EventIcon,
    AccessTime as TimeIcon,
    LocationOn as LocationIcon,
    Description as DescriptionIcon,
    Category as CategoryIcon,
    Image as ImageIcon,
    PhotoCamera as PhotoCameraIcon,
    Collections as CollectionsIcon
} from "@mui/icons-material"
import { PostMedia } from "../../components/post/card/PostMedia"
import { useNavigate, useParams } from "react-router"
import { createEvent } from "../../controllers/event/event"
import Datetime from 'react-datetime';
import "react-datetime/css/react-datetime.css";
import { createComment } from "../../controllers/posts/comments"
import { CategoryPicker } from "../../components/category.picker"
import Autocomplete from "react-google-autocomplete";
import { header_radio_set } from "../../util/util"

export const CreateEvent = () => {
    const YOUR_GOOGLE_MAPS_API_KEY = process.env.REACT_APP_YOUR_GOOGLE_MAPS_API_KEY
    const { userDetails } = useContext(UserContext)
    const token = userDetails?.token
    const [header_img, setHeaderImg] = useState('')
    const [media_ids, setMediaIds] = useState([])
    const [imageUrls, setImageUrls] = useState([])
    const [chooseHeader, setChooseHeader] = useState(true)
    const [startdate, setStartDate] = useState(new Date())
    const [enddate, setEndDate] = useState(new Date())
    const [category, setCategory] = useState('')
    const [status, setStatus] = useState('')
    const [location, setLocation] = useState('')
    const [isOnline, setIsOnline] = useState(false)
    const [meetingLink, setMeetingLink] = useState('')
    const [title, setTitle] = useState('')
    const [errors, setErrors] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { community_id } = useParams()
    const navigate = useNavigate()

    const validateForm = () => {
        const newErrors = {}
        
        if (!title.trim()) newErrors.title = 'Event title is required'
        if (!status.trim()) newErrors.description = 'Event description is required'
        if (!isOnline && !location) newErrors.location = 'Event location is required'
        if (!category) newErrors.category = 'Event category is required'
        if (startdate <= new Date()) newErrors.startDate = 'Start date must be in the future'
        if (enddate <= startdate) newErrors.endDate = 'End date must be after start date'
        
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const queryClient = new QueryClient()
    const mutation = useMutation({
        mutationFn: createEvent,
        onSuccess: (data) => {
            setIsSubmitting(false)
            queryClient.invalidateQueries({ queryKey: ['createEvent'] })
            navigate("/event/show/" + data.event._id)
        },
        onError: (error) => {
            setIsSubmitting(false)
            console.error('Error creating event:', error)
        }
    })

    const mutationComment = useMutation({
        mutationFn: createComment,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['createComment'] })
            sendCreateEvent(data.id)
        },
        onError: (error) => {
            setIsSubmitting(false)
            console.error('Error creating comment:', error)
        }
    })

    useEffect(() => {
        if (imageUrls.length > 0) {
            setHeaderImg(imageUrls[imageUrls.length - 1])
        }
    }, [imageUrls])

    useEffect(() => {
        setHeaderImg('https://storage.googleapis.com/events.makegayfriends.com/event_images/uploads/community-1.jpg')
    }, [])
    
    const sendCreateEvent = (status_id) => {
        if (!validateForm()) return
        
        setIsSubmitting(true)
        mutation.mutate({
            title,
            desc: status,
            start_date_time: startdate,
            end_date_time: enddate,
            location: isOnline ? undefined : location,
            is_online: isOnline,
            meeting_link: isOnline ? meetingLink : undefined,
            status: status_id,
            header_img,
            category,
            community: community_id,
            author: [userDetails?.id],
            token: userDetails?.token
        })
    }
  
    const send_reply = () => {
        if (!validateForm()) return
        
        setIsSubmitting(true)
        mutationComment.mutate({
            body: {
                status,
                visibility: "unlisted",
                media_ids
            },
            token
        })
    }

    return (
        <MainLoggedInLayout>
            <Container maxWidth="md" sx={{ py: 3 }}>
                <Card elevation={3} sx={{ borderRadius: 2 }}>
                    <CardHeader
                        avatar={<EventIcon color="primary" />}
                        title={
                            <Typography variant="h4" component="h1" fontWeight="bold">
                                Create New Event
                            </Typography>
                        }
                        subheader={
                            <Typography variant="body2" color="text.secondary">
                                {new Date().toLocaleDateString('en-US', { 
                                    weekday: 'long', 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric' 
                                })}
                            </Typography>
                        }
                        sx={{ pb: 1 }}
                    />
                    
                    <CardContent sx={{ px: 3 }}>
                        <Stack spacing={4}>
                            {/* Basic Event Information */}
                            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DescriptionIcon color="primary" />
                                    Event Details
                                </Typography>
                                
                                <Stack spacing={3}>
                                    <TextField
                                        fullWidth
                                        label="Event Title"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        error={!!errors.title}
                                        helperText={errors.title}
                                        variant="outlined"
                                        placeholder="Enter a catchy title for your event"
                                    />
                                    
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label="Event Description"
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                        error={!!errors.description}
                                        helperText={errors.description}
                                        variant="outlined"
                                        placeholder="Tell people what your event is about..."
                                    />
                                </Stack>
                            </Paper>

                            {/* Date and Time */}
                            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TimeIcon color="primary" />
                                    Date & Time
                                </Typography>
                                
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Box>
                                            <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                                Start Date & Time
                                            </Typography>
                                            <Box sx={{ '& .rdtPicker': { zIndex: 1300 } }}>
                                                <Datetime 
                                                    value={startdate} 
                                                    onChange={(value) => setStartDate(value._d || value)} 
                                                    inputProps={{ 
                                                        style: { 
                                                            width: '100%', 
                                                            padding: '12px', 
                                                            border: errors.startDate ? '2px solid #f44336' : '1px solid #ccc',
                                                            borderRadius: '4px',
                                                            fontSize: '16px'
                                                        } 
                                                    }}
                                                />
                                                {errors.startDate && (
                                                    <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                                                        {errors.startDate}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Grid>
                                    
                                    <Grid item xs={12} md={6}>
                                        <Box>
                                            <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                                End Date & Time
                                            </Typography>
                                            <Box sx={{ '& .rdtPicker': { zIndex: 1300 } }}>
                                                <Datetime 
                                                    value={enddate} 
                                                    onChange={(value) => setEndDate(value._d || value)} 
                                                    inputProps={{ 
                                                        style: { 
                                                            width: '100%', 
                                                            padding: '12px', 
                                                            border: errors.endDate ? '2px solid #f44336' : '1px solid #ccc',
                                                            borderRadius: '4px',
                                                            fontSize: '16px'
                                                        } 
                                                    }}
                                                />
                                                {errors.endDate && (
                                                    <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                                                        {errors.endDate}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Location */}
                            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LocationIcon color="primary" />
                                    Location
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                    <Button
                                        variant={!isOnline ? "contained" : "outlined"}
                                        onClick={() => setIsOnline(false)}
                                        size="small"
                                    >
                                        In Person
                                    </Button>
                                    <Button
                                        variant={isOnline ? "contained" : "outlined"}
                                        onClick={() => setIsOnline(true)}
                                        size="small"
                                    >
                                        Online
                                    </Button>
                                </Box>

                                {isOnline ? (
                                    <TextField
                                        fullWidth
                                        label="Meeting Link (optional)"
                                        placeholder="Paste your Zoom/Meet/call link"
                                        value={meetingLink}
                                        onChange={(e) => setMeetingLink(e.target.value)}
                                        helperText="Optional — attendees can also just check in once the party starts to link up."
                                        variant="outlined"
                                    />
                                ) : (
                                    <Box sx={{
                                        '& .form-control': {
                                            width: '100%',
                                            padding: '12px',
                                            border: errors.location ? '2px solid #f44336' : '1px solid #ccc',
                                            borderRadius: '4px',
                                            fontSize: '16px',
                                            outline: 'none',
                                            '&:focus': {
                                                borderColor: '#1976d2',
                                                boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)'
                                            }
                                        }
                                    }}>
                                        <Autocomplete
                                            className="form-control"
                                            placeholder="Enter event address or location"
                                            apiKey={YOUR_GOOGLE_MAPS_API_KEY}
                                            onPlaceSelected={(place) => setLocation(place)}
                                            options={{
                                                types: ["(regions)"],
                                                componentRestrictions: { country: "us" },
                                            }}
                                        />
                                        {errors.location && (
                                            <Typography variant="caption" color="error" sx={{ ml: 1, mt: 0.5, display: 'block' }}>
                                                {errors.location}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Paper>

                            {/* Event Image */}
                            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ImageIcon color="primary" />
                                    Event Image
                                </Typography>
                                
                                <Stack spacing={2}>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <Button
                                            variant={chooseHeader ? "contained" : "outlined"}
                                            startIcon={<CollectionsIcon />}
                                            onClick={() => setChooseHeader(true)}
                                            size="small"
                                        >
                                            Choose from Gallery
                                        </Button>
                                        <Button
                                            variant={!chooseHeader ? "contained" : "outlined"}
                                            startIcon={<PhotoCameraIcon />}
                                            onClick={() => setChooseHeader(false)}
                                            size="small"
                                        >
                                            Upload Custom Image
                                        </Button>
                                    </Box>

                                    {chooseHeader ? (
                                        <Box>
                                            <FormControl component="fieldset" sx={{ width: '100%' }}>
                                                <RadioGroup
                                                    value={header_img}
                                                    onChange={(e) => setHeaderImg(e.target.value)}
                                                    sx={{ mt: 1 }}
                                                >
                                                    <Grid container spacing={2}>
                                                        {header_radio_set.map((radio, index) => (
                                                            <Grid item xs={6} sm={4} md={3} key={index}>
                                                                <FormControlLabel
                                                                    value={`https://storage.googleapis.com/events.makegayfriends.com/event_images/uploads/${radio}`}
                                                                    control={<Radio />}
                                                                    label={
                                                                        <Box
                                                                            component="img"
                                                                            src={`https://storage.googleapis.com/events.makegayfriends.com/event_images/uploads/${radio}`}
                                                                            alt="Event option"
                                                                            sx={{
                                                                                width: '100%',
                                                                                height: 80,
                                                                                objectFit: 'cover',
                                                                                borderRadius: 1,
                                                                                border: header_img === `https://storage.googleapis.com/events.makegayfriends.com/event_images/uploads/${radio}` 
                                                                                    ? '3px solid #1976d2' : '1px solid #ddd',
                                                                                cursor: 'pointer',
                                                                                transition: 'all 0.2s',
                                                                                '&:hover': {
                                                                                    transform: 'scale(1.02)',
                                                                                    boxShadow: 2
                                                                                }
                                                                            }}
                                                                        />
                                                                    }
                                                                    sx={{ margin: 0, width: '100%' }}
                                                                />
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </RadioGroup>
                                            </FormControl>
                                        </Box>
                                    ) : (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                                Upload Custom Event Image
                                            </Typography>
                                            <PostMedia 
                                                imageUrls={imageUrls} 
                                                setImageUrls={setImageUrls} 
                                                media_ids={media_ids} 
                                                setMediaIds={setMediaIds} 
                                                token={token} 
                                            />
                                        </Box>
                                    )}
                                </Stack>
                            </Paper>

                            {/* Category */}
                            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CategoryIcon color="primary" />
                                    Category
                                </Typography>
                                
                                <Box sx={{ mt: 2 }}>
                                    <CategoryPicker 
                                        category={category} 
                                        setCategory={setCategory} 
                                    />
                                    {errors.category && (
                                        <Typography variant="caption" color="error" sx={{ ml: 1, mt: 0.5, display: 'block' }}>
                                            {errors.category}
                                        </Typography>
                                    )}
                                </Box>
                            </Paper>

                            {/* Additional Media */}
                            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                    Additional Images (Optional)
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Add more images to showcase your event
                                </Typography>
                                <PostMedia 
                                    imageUrls={imageUrls} 
                                    setImageUrls={setImageUrls} 
                                    media_ids={media_ids} 
                                    setMediaIds={setMediaIds} 
                                    token={token} 
                                />
                            </Paper>

                            {/* Error Alert */}
                            {Object.keys(errors).length > 0 && (
                                <Alert severity="error" sx={{ borderRadius: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Please fix the following errors:
                                    </Typography>
                                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                        {Object.values(errors).map((error, index) => (
                                            <li key={index}>
                                                <Typography variant="body2">{error}</Typography>
                                            </li>
                                        ))}
                                    </ul>
                                </Alert>
                            )}
                        </Stack>
                    </CardContent>
                    
                    <CardActions sx={{ p: 3, pt: 0 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={send_reply}
                            disabled={isSubmitting}
                            sx={{
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                borderRadius: 2,
                                textTransform: 'none',
                                boxShadow: 3,
                                '&:hover': {
                                    boxShadow: 6,
                                    transform: 'translateY(-1px)'
                                }
                            }}
                        >
                            {isSubmitting ? 'Creating Event...' : '🎉 Create Event'}
                        </Button>
                    </CardActions>
                </Card>
            </Container>
        </MainLoggedInLayout>
    )
}